import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import ProductHero from '../components/ProductHero';
import ScreenshotMarquee from '../components/ScreenshotMarquee';
import Workflow from '../components/Workflow';
import ScreenshotGallery from '../components/ScreenshotGallery';
import TestimonialBlock from '../components/TestimonialBlock';

const ENGLAND_SHOTS = [
  { src: '/images/nhs/nhs-portfolio-tracker.jpg', caption: 'NHS England multi-Trust portfolio tracker with capital, lifecycle and estates rollup.' },
  { src: '/images/nhs/nhs-pmo-command-centre.jpg', caption: 'PMO command centre, every Trust, every project, every stage.' },
  { src: '/images/nhs/nhs-project-riba-stages.jpg', caption: 'RIBA-aligned project stages with governance evidence at each gate.' },
  { src: '/images/nhs/nhs-board-pack.jpg', caption: 'AI-generated board pack in plain English, assembled from live data.' },
  { src: '/images/nhs/nhs-governance-reports.jpg', caption: 'Governance reports composed from the day\u2019s work, no manual re-typing.' },
  { src: '/images/nhs/nhs-compliance-rails.jpg', caption: 'Compliance rails for HTM, IPC, safety and statutory obligations.' },
  { src: '/images/nhs/nhs-ipc-dashboard.jpg', caption: 'Infection prevention & control dashboard, condition, projects, evidence.' },
  { src: '/images/healthcare/hc-handback-readiness.jpg', caption: 'Progressive handback readiness view for PFI-adjacent contracts.' },
];

const SCOTLAND_SHOTS = [
  { src: '/images/nhs/nhs-pmo-command-centre.jpg', caption: 'NHS Scotland PMO command centre across Boards and Health & Social Care Partnerships.' },
  { src: '/images/healthcare/hc-portfolio-tracker.jpg', caption: 'Territorial Board portfolio tracker, aligned to SCIM and NDAP governance.' },
  { src: '/images/healthcare/hc-project-stages.jpg', caption: 'Project stages aligned to Scottish Capital Investment Manual (SCIM) gateways.' },
  { src: '/images/healthcare/hc-governance-weekly-dialog.jpg', caption: 'Weekly governance dialogue captured against each project stage.' },
  { src: '/images/nhs/nhs-financial-systems.jpg', caption: 'Finance rails tuned to Scottish Government capital reporting cadence.' },
  { src: '/images/healthcare/hc-projects-gate-overview.jpg', caption: 'Gate-by-gate overview for OBC / FBC and SCIM assurance evidence.' },
  { src: '/images/healthcare/hc-workspace-studio.jpg', caption: 'Workspace studio, configured for Scottish Board terminology and workflows.' },
  { src: '/images/nhs/nhs-compliance-rails.jpg', caption: 'SHTM-aligned compliance rails and evidence for statutory obligations.' },
];

// Fast, visual proof strip, auto-scrolling row of real dashboards
const MARQUEE_SHOTS = [
  { src: '/images/nhs/nhs-portfolio-tracker.jpg' },
  { src: '/images/nhs/nhs-pmo-command-centre.jpg' },
  { src: '/images/healthcare/hc-dashboard.jpg' },
  { src: '/images/healthcare/hc-project-stages.jpg' },
  { src: '/images/healthcare/hc-handback-readiness.jpg' },
  { src: '/images/nhs/nhs-ipc-dashboard.jpg' },
  { src: '/images/healthcare/hc-portfolio-tracker.jpg' },
  { src: '/images/nhs/nhs-governance-reports.jpg' },
  { src: '/images/healthcare/hc-workspace-studio.jpg' },
  { src: '/images/nhs/nhs-board-pack.jpg' },
];

const COUNTRIES = [
  { name: 'NHS England', status: 'Available now',
    preview: '/images/nhs/nhs-portfolio-tracker.jpg',
    body: 'A Videra environment tuned for NHS England capital, minor works and estates delivery.',
    cta: { label: 'Try NHS England, 14 days fully unlocked', href: 'https://demo.videra.app/explore' } },
  { name: 'NHS Scotland', status: 'Available now',
    preview: '/images/nhs/nhs-pmo-command-centre.jpg',
    body: 'Configured around Scottish healthcare project and lifecycle delivery requirements.',
    cta: { label: 'Try NHS Scotland, 14 days fully unlocked', href: 'https://demo.videra.app/explore' } },
  { name: 'NHS Wales', status: 'In production',
    preview: '/images/healthcare/hc-dashboard.jpg',
    body: 'Welsh healthcare environment currently in development.',
    cta: { label: 'Register interest', to: '/contact' } },
  { name: 'Health & Social Care NI', status: 'In production',
    preview: '/images/healthcare/hc-project-stages.jpg',
    body: 'HSCNI environment currently in development.',
    cta: { label: 'Register interest', to: '/contact' } },
];

const V2VideraHealthcare = () => {
  const [nation, setNation] = useState('england');
  useSEO({
    title: 'Videra Healthcare | NHS project & lifecycle platform',
    description: 'Country-specific Videra environments for NHS England, Scotland, Wales and Northern Ireland, project delivery, lifecycle, evidence and governance.',
    path: '/videra/healthcare',
  });

  return (
    <div data-testid="v2-videra-healthcare">
      <ProductHero
        eyebrow="Videra Healthcare"
        title={<>Project & lifecycle delivery,<br />built around healthcare.</>}
        body="One environment for capital, minor works, lifecycle and estates, configured per country."
        primary={{ label: 'Try Videra Healthcare, 14 days fully unlocked', href: 'https://demo.videra.app/explore', testid: 'videra-healthcare-try' }}
        secondary={{ label: 'Talk to Keystone', to: '/contact' }}
        heroImage="/images/nhs/nhs-portfolio-tracker.jpg"
        heroImageAlt="Videra Healthcare NHS Portfolio Tracker"
        liveChips={[
          { icon: 'trend', kicker: 'Portfolio', label: '£721.3M capital', pos: 'tr', tone: 'blue' },
          { icon: 'check', kicker: 'Gate approved', label: 'RIBA 4 · Ward Block C', pos: 'bl', tone: 'emerald' },
          { icon: 'shield', kicker: 'HTM / SHTM', label: 'Evidence current', pos: 'br', tone: 'blue' },
        ]}
        testid="videra-healthcare-hero"
      />

      {/* Fast visual proof, auto-scrolling dashboards */}
      <ScreenshotMarquee
        eyebrow="Real NHS dashboards, not concept art"
        shots={MARQUEE_SHOTS}
        testid="healthcare-marquee"
      />

      {/* Country platforms, image-first cards */}
      <section className="py-16 md:py-24 bg-slate-50 border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-3">Country platforms</p>
              <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 leading-tight">One platform, tuned to each health service.</h2>
            </div>
            <p className="text-sm text-slate-500 max-w-md">Each nation has its own workflows, governance and terminology, Videra ships as a dedicated environment for each.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {COUNTRIES.map((c) => {
              const isReady = c.status === 'Available now';
              return (
                <div
                  key={c.name}
                  className="group bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-xl hover:-translate-y-0.5 hover:border-blue-500 transition-all"
                  data-testid={`healthcare-country-${c.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <div className="aspect-[16/9] bg-slate-900 relative overflow-hidden">
                    <img
                      src={c.preview}
                      alt={`${c.name} preview`}
                      loading="lazy"
                      className={`w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105 ${!isReady ? 'opacity-60' : ''}`}
                    />
                    <span className={`absolute top-3 right-3 text-[10px] uppercase tracking-[0.18em] font-semibold px-2.5 py-1 rounded ${isReady ? 'bg-emerald-500 text-white' : 'bg-amber-500 text-white'}`}>
                      {c.status}
                    </span>
                  </div>
                  <div className="p-6">
                    <p className="text-lg font-medium text-slate-900 mb-2">{c.name}</p>
                    <p className="text-sm text-slate-600 leading-relaxed mb-4">{c.body}</p>
                    {c.cta.href ? (
                      <a href={c.cta.href} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                        {c.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                      </a>
                    ) : (
                      <Link to={c.cta.to} className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 group-hover:gap-2 transition-all">
                        {c.cta.label} <ArrowRight className="w-3.5 h-3.5" />
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Workflow */}
      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs uppercase tracking-[0.22em] text-blue-600 font-semibold mb-4">The healthcare journey</p>
          <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-slate-900 mb-10">Requirement to lifecycle, in one environment.</h2>
          <Workflow testid="healthcare-workflow" steps={['Requirement', 'Governance', 'Project', 'Delivery', 'Commissioning', 'Evidence', 'Lifecycle position']} />
        </div>
      </section>

      {/* Nation gallery toggle */}
      <section className="bg-slate-50 border-t border-slate-200" data-testid="healthcare-nation-tabs">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-blue-600 font-semibold mb-1">Switch nation</p>
            <p className="text-sm text-slate-600">Each health service has its own governance and terminology. Toggle to see the environment tuned for each.</p>
          </div>
          <div className="inline-flex bg-white border border-slate-200 rounded-lg p-1 shadow-sm">
            <button
              type="button"
              onClick={() => setNation('england')}
              data-testid="healthcare-nation-england"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${nation === 'england' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:text-slate-900'}`}
            >
              NHS England
            </button>
            <button
              type="button"
              onClick={() => setNation('scotland')}
              data-testid="healthcare-nation-scotland"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${nation === 'scotland' ? 'bg-slate-900 text-white' : 'text-slate-700 hover:text-slate-900'}`}
            >
              NHS Scotland
            </button>
          </div>
        </div>
      </section>

      <ScreenshotGallery
        key={nation}
        eyebrow={nation === 'england' ? 'Inside Videra Healthcare, NHS England' : 'Inside Videra Healthcare, NHS Scotland'}
        title={nation === 'england' ? 'NHS England screens. Real workflows.' : 'NHS Scotland screens. Real workflows.'}
        description={
          nation === 'england'
            ? 'A sample of the NHS England environment, RIBA stages, HTM compliance, board packs and IPC evidence, that approved investors and evaluating Trusts see on a 14-day fully unlocked trial.'
            : 'A sample of the NHS Scotland environment, SCIM-aligned gateways, SHTM compliance, Board-level reporting and Scottish Government capital cadence, on a 14-day fully unlocked trial.'
        }
        shots={nation === 'england' ? ENGLAND_SHOTS : SCOTLAND_SHOTS}
        testid="healthcare-gallery"
      />

      <TestimonialBlock product="videra-healthcare" />
    </div>
  );
};

export default V2VideraHealthcare;
